import { Input, Form, Modal, InputNumber, Select } from 'antd';
import {
  closeModal,
  CreateLotModalVisibleSelector,
} from './CreateLotModalSlice';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { createLot, CreateLotModalDataSelector } from './CreateLotModalSlice';
import * as S from './CreateLotModal.styled';
import {
  ICategory,
  IRegion,
  IFurnishingStatus,
  ILot,
  IPurpose,
  IRentFrequency,
  IType,
} from 'interfaces/main';
import { unwrapResult } from '@reduxjs/toolkit';
import { LibrariesSelector } from 'features/Libraries/LibrariesSlice';
import { JSX } from 'react/jsx-runtime';

const { Option } = Select;

const CreateLotModal = (): JSX.Element => {
  const dispatch = useAppDispatch();

  // Инициализация формы
  const [form] = Form.useForm();

  const data = useAppSelector(CreateLotModalDataSelector);

  // Закрытие окна
  const onClose = () => {
    form.resetFields();
    dispatch(closeModal());
  };

  // Словари
  const {
    furnishingStatuses: allFurnishingStatuses,
    completedStatuses: allCompletedStatuses,
    categories: allCategories,
    types: allTypes,
    purposes: allPurposes,
    rentFrequencies: allRentFrequencies,
    regions: allRegions,
  } = useAppSelector(LibrariesSelector);

  // Отправка формы
  const onSubmit = (lot: ILot) => {
    dispatch(
      createLot({
        ...lot,
        complexId: data.complexId,
      }),
    )
      .then(unwrapResult)
      .then((org: ILot) => {
        if (org.id) {
          form.resetFields();
        }
      });
  };

  // Видимость модального окна
  const isVisible = useAppSelector(CreateLotModalVisibleSelector);

  return (
    <Modal
      open={isVisible}
      title="Create new lot"
      okText="Create"
      cancelText="Cancel"
      onCancel={onClose}
      width={700}
      onOk={() => {
        form
          .validateFields()
          .then((values) => {
            onSubmit(values);
          })
          .catch((info) => {
            console.log('Validate Failed:', info);
          });
      }}>
      <Form form={form} layout="vertical" name="groupForm" initialValues={data}>
        <S.Line>
          <Form.Item name="unit" label="Unit" style={{ width: '25%' }}>
            <Input type="text" name="unit" placeholder="106" />
          </Form.Item>
          <Form.Item name="planType" label="Plan type" style={{ width: '25%' }}>
            <Input type="text" name="planType" placeholder="B" />
          </Form.Item>
          <Form.Item name="floor" label="Floor" style={{ width: '25%' }}>
            <InputNumber
              type="text"
              placeholder="2"
              name="floor"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            name="price"
            label="Price"
            style={{ width: '25%' }}
            rules={[{ required: true, message: 'Please input price' }]}>
            <InputNumber
              type="text"
              name="price"
              placeholder="24000"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </S.Line>
        <S.Line>
          <Form.Item
            name="totalArea"
            style={{ width: '25%' }}
            label="Total area"
            rules={[
              { required: true, message: "Please input lot's total area" },
            ]}>
            <InputNumber
              type="text"
              placeholder="1000"
              name="totalArea"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            name="suiteArea"
            style={{ width: '25%' }}
            label="Suite area">
            <InputNumber
              type="text"
              placeholder="1000"
              name="suiteArea"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            name="balconyArea"
            style={{ width: '25%' }}
            label="Balcony area">
            <InputNumber
              type="text"
              placeholder="100"
              name="balconyArea"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item name="plotArea" style={{ width: '25%' }} label="Plot area">
            <InputNumber
              type="text"
              placeholder="1000"
              name="plotArea"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </S.Line>
        <S.Line>
          <Form.Item name="rooms" style={{ width: '50%' }} label="Bedrooms">
            <InputNumber
              type="text"
              placeholder="1"
              name="rooms"
              min={0}
              max={6}
              step={1}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item name="baths" style={{ width: '50%' }} label="Baths">
            <InputNumber
              type="text"
              placeholder="1"
              name="baths"
              min={1}
              max={6}
              step={1}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </S.Line>

        <S.Line>
          <Form.Item
            name="categoryId"
            style={{ width: '25%' }}
            label="Category"
            rules={[
              { required: true, message: "Please select lot's category" },
            ]}>
            <Select style={{ width: '100%' }} placeholder="Please select">
              {allCategories.map((item: ICategory) => (
                <Option key={item.id} value={item.id}>
                  {item.title_en}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="isArchived"
            label="Is archived"
            style={{ width: '25%' }}
            rules={[{ required: true, message: 'Please select field' }]}>
            <Select style={{ width: '100%' }} placeholder="Please select">
              <Option value={true}>Yes</Option>
              <Option value={false}>No</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="typeId"
            style={{ width: '25%' }}
            label="Type"
            rules={[
              { required: true, message: 'Please select property type' },
            ]}>
            <Select style={{ width: '100%' }} placeholder="Please select">
              {allTypes.map((item: IType) => (
                <Option key={item.id} value={item.id}>
                  {item.name_en}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="purposeId"
            style={{ width: '25%' }}
            label="Purpose"
            rules={[{ required: true, message: 'Please select purpose' }]}>
            <Select style={{ width: '100%' }} placeholder="Please select">
              {allPurposes.map((item: IPurpose) => (
                <Option key={item.id} value={item.id}>
                  {item.name_en}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </S.Line>
        <S.Line>
          <Form.Item
            name="furnishingStatusId"
            style={{ width: '25%' }}
            label="Furnishing status"
            rules={[
              { required: true, message: 'Please select furnishing status' },
            ]}>
            <Select style={{ width: '100%' }} placeholder="Please select">
              {allFurnishingStatuses.map((item: IFurnishingStatus) => (
                <Option key={item.id} value={item.id}>
                  {item.name_en}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="completedStatusId"
            style={{ width: '25%' }}
            label="Completed status"
            rules={[
              { required: true, message: 'Please select completed status' },
            ]}>
            <Select style={{ width: '100%' }} placeholder="Please select">
              {allCompletedStatuses.map((item: IPurpose) => (
                <Option key={item.id} value={item.id}>
                  {item.name_en}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="regionId"
            style={{ width: '25%' }}
            label="Region"
            rules={[{ required: true, message: 'Please select Region' }]}>
            <Select style={{ width: '100%' }} placeholder="Please select">
              {allRegions.map((item: IRegion) => (
                <Option key={item.id} value={item.id}>
                  {item.name_en}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="rentFrequencyId"
            style={{ width: '25%' }}
            label="Rent frequency">
            <Select style={{ width: '100%' }} placeholder="Please select">
              {allRentFrequencies.map((item: IRentFrequency) => (
                <Option key={item.id} value={item.id}>
                  {item.name_en}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </S.Line>
      </Form>
    </Modal>
  );
};

export default CreateLotModal;
